import Link from 'next/link';
import Image from 'next/image';
import { Markup } from 'interweave';
import { apolloClientSSR } from 'graphql/apollo-client';
import { BLOG_POSTS, CATEGORIES } from 'graphql/queries';
import useScrollRestoration from 'hooks/UI/useScrollRestoration';
import parseHTML from 'utils/parseHTML';
import styled, { th, useViewportWidth } from '@xstyled/styled-components';
import SEO from '@/SEO';
import Layout from '@/UI/Layout';
import { FlexBox } from '@/UI/Primitives/FlexBox';
import { Container } from '@/UI/Layout/Container';
import { SectionTitleSmall } from '@/UI/Primitives/Headings';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const BlogContainer = styled.section`
  padding: 4rem 0 12rem 1rem;

  h2 {
    padding-bottom: 2.5rem;
  }

  @media (max-width: desktop) {
    padding: 1rem 1rem 4rem;
  }
`;

const MainEntries = styled(FlexBox)`
  flex-direction: column;

  @media (min-width: 865px) and (max-width: desktop) {
    flex-direction: row;
  }
`;

const BlogEntry = styled.divBox`
  min-height: ${({ height }) => height && height};
  width: 100%;
  padding: 3rem 0;
  display: grid;
  grid-template-columns: 400px 1fr;
  border-bottom: ${({ border }) => border && '1px solid #d8d9e5'};

  @media (max-width: desktop) {
    display: flex;
    border-bottom: none;
  }

  img {
    border-radius: 15px;

    &:hover {
      opacity: 0.7;
    }
  }
`;

const EntryInfo = styled.divBox`
  position: relative;
  height: 200px;
  display: flex;
  flex-direction: column;
  margin-top: -3.75rem;
  margin-left: 1rem;

  @media (max-width: desktop) {
    max-width: 400px;
  }

  a#post_link,
  a#main_post_link {
    position: absolute;
    right: 1rem;
    align-self: flex-end;
    font-size: xxsmall;
    text-decoration: underline;
  }

  a#post_link {
    bottom: -1rem;
  }

  a#main_post_link {
    bottom: -4rem;
  }

  h5 {
    line-height: 1.3;
    font-size: ${({ textSize }) =>
      textSize === 'large' ? th.fontSize('xlarge') : th.fontSize('medium')};

    @media (max-width: desktop) {
      font-size: medium;
    }

    &:hover {
      color: gray-300;
    }
  }

  p {
    padding-top: 1rem;
    max-width: 660px;
    font-size: ${({ textSize }) =>
      textSize === 'large' ? th.fontSize('small') : th.fontSize('xsmall')};

    @media (max-width: desktop) {
      font-size: xsmall;
    }
  }
`;

export default function Blog({ posts, categories }) {
  const { t } = useTranslation();

  const viewportWidth = useViewportWidth();

  // Restore scroll position when returning from a post
  // useScrollRestoration({ data: posts });

  return (
    <Layout categories={categories}>
      <SEO
        seo={{
          title: 'Blogi - Baltbaby'
        }}
      />
      <BlogContainer>
        <Container>
          <SectionTitleSmall>{t('header.blog')}</SectionTitleSmall>
          <MainEntries>
            {posts
              ?.slice(0, viewportWidth >= 1024 ? 2 : posts?.length)
              .map(post => {
                return (
                  <BlogEntry
                    key={post.identifier}
                    border
                    flexDirection={{ _: 'column', desktop: 'row' }}
                    spaceX={{ _: 0, desktop: '7.5rem' }}
                    spaceY={{ _: '1rem', desktop: 0 }}
                  >
                    <Link href={`/blogi/${post.identifier}`}>
                      <a>
                        <Image
                          src={post.featured_image}
                          alt={post.title}
                          width={400}
                          height={280}
                        />
                      </a>
                    </Link>
                    <EntryData post={post} id="post_link" textSize="large" />
                  </BlogEntry>
                );
              })}
          </MainEntries>
          <FlexBox
            mt="1rem"
            display={{ _: 'none', desktop: 'grid' }}
            gridTemplateColumns="repeat(3, 1fr)"
            columnGap="1rem"
            rowGap="2rem"
          >
            {posts?.slice(2).map(post => {
              return (
                <BlogEntry
                  key={post.identifier}
                  display="flex"
                  flexDirection="column"
                  height="520px"
                  p="2rem 0"
                >
                  <Link href={`/blogi/${post.identifier}`}>
                    <a>
                      <Image
                        src={post.featured_image}
                        alt={post.title}
                        width={372}
                        height={248}
                      />
                    </a>
                  </Link>
                  <EntryData post={post} id="post_link" mt="2.5rem" />
                </BlogEntry>
              );
            })}
          </FlexBox>
        </Container>
      </BlogContainer>
    </Layout>
  );
}

const EntryData = ({ post, id, ...rest }) => {
  const parsedContent = parseHTML(post?.short_content);

  const content = `${parsedContent.substring(0, 206)}${
    parsedContent.length > 206 ? '...' : ''
  }`;

  return (
    <EntryInfo {...rest}>
      <Link href={`/blogi/${post.identifier}`}>
        <a>
          <h5>{post.title}</h5>
        </a>
      </Link>
      <Markup content={content} blockList={['div']} tagName="p" />
      <Link href={`/blogi/${post.identifier}`}>
        <a id={id}>Loe edasi &gt;</a>
      </Link>
    </EntryInfo>
  );
};

export async function getStaticProps(context) {
  const query = (query, variables) => {
    return apolloClientSSR(context.locale).query({
      query,
      variables,
      context: {
        fetchOptions: { method: 'GET' }
      }
    });
  };
  const categoriesPromise = query(CATEGORIES, { ids: '2' });

  const blogPostsPromise = query(BLOG_POSTS);

  const [blogPosts, categories] = await Promise.all([
    blogPostsPromise,
    categoriesPromise
  ]);

  return {
    props: {
      posts: blogPosts.data?.blogPosts.items ?? null,
      categories: categories.data.categoryList
        ? categories.data.categoryList[0].children
        : [],
        locale : context.locale ? context.locale : null,
      ...(await serverSideTranslations(context.locale))
    },
    revalidate: 600 // seconds
  };
}
